import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { LayoutDocument } from '../lib/form-layout/types';
import type { ContractFormFieldName } from '../lib/form-layout/contract-fields';

export type ContractLayoutDocument = LayoutDocument<ContractFormFieldName>;

export interface ResolvedLayout {
  layout: ContractLayoutDocument;
  scope: 'global' | 'project' | 'default';
  resolved_from: 'project' | 'global' | 'none';
  source_id: string | null;
  updated_at: string | null;
  notes: string | null;
}

export const contractFormLayoutKeys = {
  all: ['contract-form-layout'] as const,
  active: (projectId: string | null) =>
    [...contractFormLayoutKeys.all, 'active', projectId] as const,
  adminActive: (scope: 'global' | 'project', projectId: string | null) =>
    [...contractFormLayoutKeys.all, 'admin', 'active', scope, projectId] as const,
};

export function useContractFormLayout(projectId: string | null) {
  return useQuery({
    queryKey: contractFormLayoutKeys.active(projectId),
    staleTime: 60_000,
    queryFn: () =>
      api.get<ResolvedLayout>(
        '/contract-form-layout',
        projectId ? { project_id: projectId } : undefined,
      ),
  });
}

export function useAdminActiveContractLayout(
  scope: 'global' | 'project',
  projectId: string | null,
) {
  return useQuery({
    queryKey: contractFormLayoutKeys.adminActive(scope, projectId),
    queryFn: () => {
      const q: Record<string, string> = { scope };
      if (scope === 'project' && projectId) q.project_id = projectId;
      return api.get<ResolvedLayout>('/admin/contract-form-layout/active', q);
    },
    enabled: scope === 'global' || projectId !== null,
  });
}

export interface UpdateContractLayoutInput {
  scope: 'global' | 'project';
  project_id?: string | null;
  layout: ContractLayoutDocument;
  notes?: string | null;
}

export function useUpdateContractFormLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateContractLayoutInput) =>
      api.put<ResolvedLayout>('/admin/contract-form-layout', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractFormLayoutKeys.all }),
  });
}
